// BewerbungsFragebogen.jsx
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import './BewerbungsFragebogen.scss';

/* -------------------------
   Fragen-Definitionen
------------------------- */
const entryQuestion = {
  id: "role",
  question: "Für welche Stelle interessierst Du dich?",
  options: [
    { label: "Reinigungskraft (m/w/d)", icon: false },
  ],
};

const questionsForAzubi = [

  {
    id: "abschluss",
    question: "Welchen Schulabschluss hast du oder wirst du bald erreichen?",
    options: [
      { label: "Qualifizierender Mittelschulabschluss", icon: false },
      { label: "Mittlere Reife", icon: false },
      { label: "Noch kein Abschluss", icon: false },
      { label: "Anderer Abschluss", icon: false },
    ],
  },
  {
    id: "beginn",
    question: "Wann könntest du mit der Ausbildung starten?",
    options: [
      { label: "sofort", icon: false },
      { label: "innerhalb der nächsten 3 Monate", icon: false },
      { label: "zum Ausbildungsstart (September)", icon: false },
    ],
  },
];

const questionsForGeselle = [
  {
    id: "abschluss",
    question: "Hast Du eine abgeschlossene Berufsausbildung?",
    options: [
      { label: "Ja", icon: false },
      { label: "Nein", icon: false },
    ],
  },
  {
    id: "erfahrung",
    question: "Wie viele Jahre Berufserfahrung hast du?",
    options: [
      { label: "Keine", icon: false },
      { label: "1–3 Jahre", icon: false },
      { label: "3–5 Jahre", icon: false },
      { label: "> 5 Jahre", icon: false },
    ],
  },
  {
    id: "beginn",
    question: "Wann wäre dein frühestmöglicher Eintritt bei uns?",
    options: [
      { label: "sofort", icon: false },
      { label: "1-3 Monate", icon: false },
      { label: "3-5 Monate", icon: false },
      { label: "5-8 Monate", icon: false },
    ],
  }, 
];

const finalTextQuestion = {
  id: "zusatz",
  question: "Möchtest du uns noch was über Dich erzählen? (optional)",
  isTextArea: true,
};

/* -------------------------
   Komponente
------------------------- */
const BewerbungsFragebogen = () => {
  const [role, setRole] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [contact, setContact] = useState({ name: "", telefon: "", email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [sendDataSuccess, setSendDataSuccess] = useState(false);

  // nodeRef für Transition
  const nodeRef = useRef(null);

  const getQuestions = () => {
    switch (role) {
      case "Ausbildung zum Gebäudereiniger (m/w/d)":
      case "Ausbildung Kaufmann/frau für Büromanagement (m/w/d)":
        return [entryQuestion, ...questionsForAzubi, finalTextQuestion];
      case "Reinigungskraft (m/w/d)":
        return [entryQuestion, ...questionsForGeselle, finalTextQuestion];
      default:
        return [entryQuestion];
    }
  };

  const questions = getQuestions();
  const currentQuestion = questions[step];
  const totalQuestions = questions.length;
  const showProgress = step !== 0 && !showContactForm;
  const currentQuestionNumber = step + 1;

  const handleAnswer = (value) => {
    const updatedAnswers = { ...answers, [currentQuestion.id]: value };
    if (currentQuestion.id === "role") {
      setRole(value);
      setAnswers(updatedAnswers);
      setStep(1);
    } else {
      setAnswers(updatedAnswers);
      if (step < questions.length - 1) {
        setStep(step + 1);
      } else {
        setShowContactForm(true);
      }
    }
  };

  const handleBack = () => {
    if (showContactForm) {
      setShowContactForm(false);
      setStep(questions.length - 1);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleContactSubmit = () => {
    const dataToSend = { ...answers, ...contact };
    if (!contact.name || !contact.email || !contact.telefon) {
      alert("Bitte Name, E-Mail und Telefon ausfüllen.");
      return;
    }
    setIsLoading(true);
    axios({
      method: 'post',
      url: '/backend/index.php',
      headers: { 'Content-Type': 'application/json' },
      data: dataToSend
    })
      .then(() => {
        setSendDataSuccess(true);
        setShowContactForm(false);
      })
      .catch(() => {
        alert("Fehler beim Senden. Bitte versuchen Sie es später erneut.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="fragebogen-container">
      {sendDataSuccess ? (
        <div className="success-box">
          🎉 Vielen Dank! Deine Bewerbung wurde erfolgreich gesendet. <br />
          Wir melden uns zeitnah bei Dir.
        </div>
      ) : showContactForm ? (
        <>
          <h2 className="headline">Deine Kontaktdaten</h2>
          <input
            type="text"
            placeholder="Name*"
            value={contact.name}
            onChange={(e) => setContact({ ...contact, name: e.target.value })}
            className="input-field"
          />
          <input
            type="tel"
            placeholder="Telefon*"
            value={contact.telefon}
            onChange={(e) => setContact({ ...contact, telefon: e.target.value })}
            className="input-field"
          />
          <input
            type="email"
            placeholder="E-Mail*"
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            className="input-field"
          />
          <p className="datenschutz-text">
            Wir verarbeiten Ihre Daten zum Zwecke der Bearbeitung Ihrer Anfrage. Bitte beachten Sie unsere <a href="/datenschutz" target="_blank">Datenschutzerklärung</a>.
          </p>
          <button onClick={handleContactSubmit} disabled={isLoading} className="btn btn-primary">
            {isLoading ? "Sende..." : "Absenden"}
          </button>
          <button onClick={handleBack} className="btn btn-secondary">Zurück</button>
        </>
      ) : (
        <>
          {showProgress && (
            <>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${(currentQuestionNumber / totalQuestions) * 100}%` }}
                />
              </div>
              <div className="progress-text">
                Frage {currentQuestionNumber} von {totalQuestions}
              </div>
            </>
          )}

          {/* Fragen mit Animation */}
          <div className="question-container" style={{ height: nodeRef.current?.offsetHeight || 'auto' }}>
            <TransitionGroup component={null}>
              {questions.length > 0 && (
                (() => {
                  const nodeRef = React.createRef(); // 👈 eigener Ref pro Render
                  return (
                    <CSSTransition
                      key={currentQuestion.id}
                      timeout={300}
                      classNames="slide"
                      nodeRef={nodeRef}
                      unmountOnExit
                    >
                      <div ref={nodeRef} className="question-wrapper">
                        <h2 className="headline">{currentQuestion.question}</h2>
                        <div className="options-list">
                          {currentQuestion.isTextArea ? (
                            <>
                              <textarea
                                rows={5}
                                className="textarea-field"
                                placeholder="Deine Nachricht hier..."
                                onChange={(e) =>
                                  setAnswers({ ...answers, [currentQuestion.id]: e.target.value })
                                }
                              />
                              <button
                                onClick={() => setShowContactForm(true)}
                                className="btn btn-primary"
                              >
                                Weiter
                              </button>
                            </>
                          ) : (
                            currentQuestion.options.map((opt, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleAnswer(opt.label)}
                                className="btn btn-option"
                              >
                                {opt.label}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </CSSTransition>
                  );
                })()
              )}
            </TransitionGroup>
          </div>

          {step > 0 && (
            <button onClick={handleBack} className="btn btn-secondary">Zurück</button>
          )}
        </>
      )}
    </div>
  );
};

export default BewerbungsFragebogen;
